import {
  faCog,
  faDesktop,
  faServer,
  faWrench,
  type IconDefinition,
} from "@fortawesome/pro-solid-svg-icons";
import type { AppType } from "../api/types";

export const APP_TYPE_CONFIG: Record<
  AppType,
  { icon: IconDefinition; label: string; description: string }
> = {
  spa: {
    icon: faDesktop,
    label: "SPA",
    description: "Browser single-page app. Users sign in with PKCE. ",
  },
  api: {
    icon: faServer,
    label: "API",
    description:
      "Backend API, validates incoming tokens and can call other services",
  },
  worker: {
    icon: faCog,
    label: "Other service",
    description:
      "Outbound-only workload: background job, integration, MCP server, or agent. ",
  },
  custom: {
    icon: faWrench,
    label: "Custom",
    description: "Non-standard configuration, managed by the Intility ID team.",
  },
};

// The creatable types. "custom" is display-only (ADR-0044) and never offered here.
export const APP_TYPES: AppType[] = ["spa", "api", "worker"];

export const APP_TYPE_NAME_SUFFIX: Record<AppType, string> = {
  spa: "Frontend",
  api: "API",
  worker: "Worker",
  custom: "Client",
};

// What each type actually creates in Keycloak, shown at the moment of choice so
// the operator sees the real consequence before creating (ADR-0044).
export const APP_TYPE_FACTS: Record<AppType, string[]> = {
  spa: [
    "Public browser client — holds no secret",
    "Users sign in with authorization code + PKCE",
    "No service account",
    "Can never receive API access grants from other clients",
  ],
  api: [
    "Confidential service client with a service account",
    "Authenticates with a SPIFFE workload identity or a client secret",
    "Exposes an API — other clients can be granted access to call it",
    "Can change to Other service later, if no client holds its audience",
  ],
  worker: [
    "Confidential service client with a service account",
    "Authenticates with a SPIFFE workload identity or a client secret",
    "Outbound only — no client can call it. This is the secure default.",
    "Can change to API later, if it grows an inbound API",
  ],
  custom: ["Non-standard configuration, managed by the Intility ID team"],
};

// The purposes a client can be given, by type: API, SPA, AI Agent, MCP and
// Worker. The vocabulary is closed and canonical in keycloak-api
// (ClientPurposes), which validates every write; this list is the portal's
// per-type copy of it, as it keeps a copy of the stacks. An SPA has two stored
// purposes, a web application and a Swagger application, that are both shown
// as "SPA".
export const PURPOSE_TAGS_BY_TYPE: Record<
  AppType,
  { value: string; label: string }[]
> = {
  spa: [
    { value: "web-app", label: "SPA" },
    { value: "swagger-app", label: "SPA" },
  ],
  api: [
    { value: "api", label: "API" },
    { value: "mcp-server", label: "MCP" },
    { value: "ai-agent", label: "AI Agent" },
  ],
  worker: [
    { value: "worker", label: "Worker" },
    { value: "mcp-server", label: "MCP" },
    { value: "ai-agent", label: "AI Agent" },
  ],
  custom: [],
};

// Purposes that clients registered before the list above still carry, shown as
// the purpose that replaced them.
const LEGACY_PURPOSE_LABELS: Record<string, string> = {
  spa: "SPA",
  "background-job": "Worker",
  integration: "Worker",
};

// The label of a purpose the portal knows for that type: from the list for the
// type, or one of the purposes that were replaced. Undefined for any other.
function knownPurposeLabel(
  appType: AppType,
  purposeTag: string,
): string | undefined {
  return (
    PURPOSE_TAGS_BY_TYPE[appType]?.find((p) => p.value === purposeTag)?.label ??
    LEGACY_PURPOSE_LABELS[purposeTag]
  );
}

// The label of a client's purpose, or the tag itself when it is not known.
export function purposeLabel(appType: AppType, purposeTag: string): string {
  return knownPurposeLabel(appType, purposeTag) ?? purposeTag;
}

export function defaultPurpose(appType: AppType): string {
  const opts = PURPOSE_TAGS_BY_TYPE[appType] ?? [];
  // The purpose named after the type, else the first one of the type.
  const match = opts.find((o) => o.value === appType) ?? opts[0];
  return match?.value ?? "";
}

// The client stacks with a dedicated SDK guide (ADR-0060 decision 6). The list
// is closed and canonical in keycloak-api (ClientStacks); the Portal keeps a
// copy, as it does for purpose tag. A value joins the list when a guide for it
// exists. Any other value is accepted as written and displays without a guide.
export const KNOWN_STACKS: { value: string; label: string }[] = [
  { value: "react", label: "React" },
  { value: "dotnet", label: ".NET" },
];

export function isKnownStack(stack: string | null): boolean {
  return (
    stack != null && KNOWN_STACKS.some((s) => s.value === stack.toLowerCase())
  );
}

// Which client kinds each known stack's SDK covers: the TypeScript SDK is the
// browser login, the .NET SDK is inbound validation and service identity. A
// known stack on an uncovered kind (a react API, say) has no SDK either, so
// the client builds its configuration itself, the same as an unknown stack.
const SDK_KINDS: Record<string, AppType[]> = {
  react: ["spa"],
  dotnet: ["api", "worker"],
};

export function hasSdk(stack: string | null, appType: AppType): boolean {
  return SDK_KINDS[stack?.toLowerCase() ?? ""]?.includes(appType) ?? false;
}

// What a client is called in a badge or a list: its purpose (API, SPA, AI
// Agent, MCP, Worker). A client with no purpose, such as the platform's own
// services, or one the portal does not know for its type (a stray tag such as
// "cli", or "web-app" on an API), gets the default purpose of its type; the
// custom type has none, so it says "Custom".
export function clientKindLabel(
  appType: AppType,
  purposeTag: string | null | undefined,
): string {
  const known = purposeTag ? knownPurposeLabel(appType, purposeTag) : undefined;
  if (known) return known;
  const fallback = defaultPurpose(appType);
  if (fallback) return purposeLabel(appType, fallback);
  return APP_TYPE_CONFIG[appType]?.label ?? appType;
}

// Mirrors the server-side slug derivation: lowercase, non-alphanumeric → hyphen.
// Used to preview the clientId a name will produce when no explicit slug is given.
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/g, "");
}

// The creation date as dd/mm/yyyy in the user's local timezone; "—" when the
// client has none.
export function formatCreatedDate(createdAt: string | null): string {
  if (!createdAt) return "—";
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB");
}

export function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  // Local calendar date, not the UTC one: the API sends UTC instants, and a
  // toISOString().slice() would print the previous day for evening UTC times
  // in timezones ahead of UTC (the portal's audience is UTC+1/+2).
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
